import {
    CreateExtArgs,
    DmApis,
    DocumentDataTypes,
    Extension,
    KeyValPredicate,
    PointerMethods,
    pointerUtils,
    ExtensionAPI,
    type DalItem,
    type ExtensionPublicAPI
} from '@wix/document-manager-core'
import type {Pointer, RefArray, BreakpointRelation, VariantRelation, CompRef} from '@wix/document-services-types'
import _ from 'lodash'
import * as constants from '../constants/constants'
import {refArray, breakpointRelation, variantRelation, stripHashIfExists} from '../utils/refArrayUtils'
import type {PageAPI} from './page'
import {COMP_DATA_QUERY_KEYS_WITH_STYLE, MASTER_PAGE_ID, RELATION_DATA_TYPES} from '../constants/constants'
import {getLanguageByUseOriginal} from './page/language'
import type {AddItemOptions, DataModelExtensionAPI} from './dataModel/dataModel'
import {autoType, builderTypes, getComp, validateLanguageCode} from '../utils/builderUtils'
import {ReportableError} from '@wix/document-manager-utils'
import type {SchemaExtensionAPI} from './schema/schema'
const {getPointer, getInnerPointer} = pointerUtils
const DATA_TYPES = _.mapKeys(constants.DATA_TYPES)
const masterPageId = 'masterPage'

type Predicate = KeyValPredicate | Record<string, any>

const onError = (message: string, extras: any = {}) => {
    throw new ReportableError({errorType: 'DataExtensionError', message, ...extras})
}

export const createItemGetter = (itemType: string) => (itemId: string, pageId: string) => ({
    ...getPointer(itemId, itemType),
    pageId
})

const createPointersMethods = ({dal, pointers, extensionAPI}: DmApis): PointerMethods => {
    const getItem = (itemType: string, itemId: string, pageId: string) => ({...getPointer(itemId, itemType), pageId}) //adding pageId to pointer. hack for now
    const getDataItem = createItemGetter(DATA_TYPES.data)
    const getSlotsItem = createItemGetter(DATA_TYPES.slots)
    const getDesignItem = createItemGetter(DATA_TYPES.design)
    const getBehaviorsItem = createItemGetter(DATA_TYPES.behaviors)
    const getPropertyItem = createItemGetter(DATA_TYPES.props)
    const getThemeItem = createItemGetter(DATA_TYPES.style)
    const getConnectionsItem = createItemGetter(DATA_TYPES.connections)
    const getMobileHintsItem = createItemGetter(DATA_TYPES.mobileHints)
    const getAnchorsItem = createItemGetter(DATA_TYPES.anchors)
    const getDataByQueryPointer = (query: string, pageId: string, dataType: string) => {
        pageId = pageId || constants.MASTER_PAGE_ID
        dataType = dataType || DATA_TYPES.data

        return getItem(dataType, query, pageId)
    }
    const getBreakpointsDataItem = createItemGetter(DATA_TYPES.breakpoints)
    const getVariantsDataItem = createItemGetter(DATA_TYPES.variants)
    const getEffectsDataItem = createItemGetter(DATA_TYPES.effects)
    const getTransformationsDataItem = createItemGetter(DATA_TYPES.transformations)
    const getTransitionsDataItem = createItemGetter(DATA_TYPES.transitions)
    const getReactionsDataItem = createItemGetter(DATA_TYPES.reactions)

    const getKeyframeEffectDataItem = (id: string) => getItem(DATA_TYPES.effects, id, MASTER_PAGE_ID)

    const getAllStylesInPage = (pageId: string) => ({type: 'page', id: pageId, innerPath: ['data', 'theme_data']})

    const getPageIdOfData = (pointer: Pointer) =>
        dal.get(getPointer(pointer.id, pointer.type, {innerPath: ['metaData', 'pageId']}))

    const getTranslatedData = (pointer: Pointer, languageCode?: string): Pointer => {
        const pageId = getPageIdOfData(pointer) || pointer.pageId || constants.MASTER_PAGE_ID
        const originalLanguage = dal.get(getInnerPointer(pointers.multilingual.originalLanguage(), 'languageCode'))

        if (languageCode === originalLanguage || !languageCode) {
            return pointer
        }

        return pointers.multilingualTranslations.translationDataItem(pageId, languageCode, pointer.id)
    }

    const getItemsWithPredicate = (namespace: string, predicate: Predicate = {}, pageId: string = 'masterPage') => {
        const pred: KeyValPredicate = typeof predicate === 'object' ? _.matches(predicate) : predicate
        const pageCompFilter = (extensionAPI.page as PageAPI).getPageIndexId(pageId)
        const result = dal.query(namespace, pageCompFilter, pred)
        return _(result)
            .keys()
            .map(id => getItem(namespace, id, pageId))
            .value()
    }

    const findItemWithPredicate = (type: string, predicate: Predicate = {}, pageId: string = 'masterPage') => {
        predicate = typeof predicate === 'object' ? _.matches(predicate) : predicate
        const pageCompFilter = (extensionAPI.page as PageAPI).getPageIndexId(pageId)
        const item = dal.find(type, pageCompFilter, predicate as KeyValPredicate)
        return item && getItem(type, item.value.id, pageId)
    }

    const getPermanentNodes = (namespace: string) => {
        return getItemsWithPredicate(namespace, v => {
            const type = dal.schema.getSchemaType(namespace, v)
            return dal.schema.isPermanentDataType(namespace, type)
        })
    }
    const getDataItemsWithPredicate = (predicate: Predicate, pageId: string) =>
        getItemsWithPredicate(DATA_TYPES.data, predicate, pageId)
    const getLayoutItemsWithPredicate = (predicate: Predicate, pageId: string) =>
        getItemsWithPredicate(DATA_TYPES.layout, predicate, pageId)
    const getStyleItemsWithPredicate = (predicate: Predicate, pageId: string) =>
        getItemsWithPredicate(DATA_TYPES.style, predicate, pageId)
    const getDataItemWithPredicate = (predicate: Predicate, pageId: string) =>
        findItemWithPredicate(DATA_TYPES.data, predicate, pageId)
    const getVariantItemsWithPredicate = (predicate: Predicate, pageId: string) =>
        getItemsWithPredicate(DATA_TYPES.variants, predicate, pageId)
    const getReactionItemsWithPredicate = (predicate: Predicate, pageId: string) =>
        getItemsWithPredicate(DATA_TYPES.reactions, predicate, pageId)

    const getDesignItemsWithPredicate = (predicate: Predicate, pageId: string) =>
        getItemsWithPredicate(DATA_TYPES.design, predicate, pageId)

    const getDataItemFromMaster = (id: string) => getDataItem(id, masterPageId)

    const dataPointers = {
        getItem,
        getDataItem,
        getSlotsItem,
        getDesignItem,
        getBehaviorsItem,
        getPageIdOfData,
        getPropertyItem,
        getThemeItem,
        getMobileHintsItem,
        getConnectionsItem,
        getBreakpointsDataItem,
        getVariantsDataItem,
        getEffectsDataItem,
        getKeyframeEffectDataItem,
        getAnchorsItem,
        getTransformationsDataItem,
        getTransitionsDataItem,
        getReactionsDataItem,
        getItemsWithPredicate,
        getPermanentNodes,
        getDataItemsWithPredicate,
        getLayoutItemsWithPredicate,
        getStyleItemsWithPredicate,
        getDataItemWithPredicate,
        getVariantItemsWithPredicate,
        getReactionItemsWithPredicate,
        getDesignItemsWithPredicate,
        getDataItemFromMaster,
        getDataByQueryPointer,
        getAllStylesInPage,
        getTranslatedData
    }

    const generalPointers = {
        getAllStylesInPage
    }

    return {
        data: dataPointers,
        general: generalPointers,
        full: {
            // @ts-expect-error
            data: dataPointers,
            // @ts-expect-error
            general: generalPointers
        }
    }
}

const variantsToIdString = (pointer: Pointer): string => {
    return pointer.variants
        ? pointer.variants
              .map(variantObj => variantObj.id)
              .sort()
              .reduce((res, variant) => `${res}:v:${variant}`, '')
        : ''
}

const getUniqueId = (pointer: Pointer): string => {
    let uniqueId: string = ''
    uniqueId += `${pointer.type}`
    uniqueId += `--${pointer.id}`
    uniqueId += variantsToIdString(pointer)
    return uniqueId
}

const getDocumentDataTypes = (): DocumentDataTypes => _.mapValues(DATA_TYPES, () => ({hasSignature: true}))

export interface DataExtensionAPI extends ExtensionAPI {
    data: {
        get(compRef: CompRef, languageCode?: string): DalItem | undefined
        getItemType(pointer: Pointer): DalItem['type'] | undefined
        getInLanguage(pageId: string, dataId: string, languageCode?: string): DalItem | undefined
        getTranslationOverrides(compRef: CompRef, languageCode: string): DalItem | undefined
        update(compRef: CompRef, dataItem: DalItem, languageCode?: string): void
        updateInLanguage(
            compOrInnerElementPointer: Pointer,
            dataItem: DalItem & {type: string},
            pageId: string,
            dataId: string,
            languageCode?: string
        ): void
        removeTranslationOverrides(compRef: CompRef, languageCode: string): void
        removeTranslationOverridesForAField(compRef: CompRef, languageCode: string, field: string): void
        query(namespace: string, pageId?: string | null, pred?: (a: any) => boolean): any
        queryKeys(namespace: string, pageId?: string | null, pred?: KeyValPredicate): string[]
        refArray: {
            extractValues(obj: RefArray): any[]
            extractValuesWithoutHash(obj: RefArray): any[]
            create(values: any[]): Partial<RefArray>
            isRefArray(obj: RefArray): boolean
            update(obj: RefArray, values: any): Partial<RefArray>
            getDataPointersFromRefArray(refArrayOrValuePointer: Pointer, pageId: string): Pointer[]
        }
        getUniqueId(pointer: Pointer): string
        breakpointRelation: {
            extractBreakpoint(obj: BreakpointRelation): any
            extractRef(obj: BreakpointRelation): any
            extractRefWithoutHash(obj: BreakpointRelation): any
            create(breakpoint: string, ref: string): Partial<BreakpointRelation>
            isBreakpointRelation(obj: BreakpointRelation): any
        }
        variantRelation: {
            extractVariants(obj: VariantRelation): any
            extractRemoteVariants(obj: VariantRelation): any
            extractAllVariants(obj: VariantRelation): any
            extractTo(obj: VariantRelation): any
            extractFrom(obj: VariantRelation): any
            create(variants: string[], from: string, to: string): Partial<VariantRelation>
            isVariantRelation(obj: VariantRelation): boolean
        }
    }
}

const createExtensionAPI = ({dal, extensionAPI, pointers}: CreateExtArgs): DataExtensionAPI => {
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const getDataPointersFromRefArray = (refArrayOrValuePointer: Pointer, pageId: string): Pointer[] => {
        const itemType = refArrayOrValuePointer.type
        const refArrayOrValue =
            refArrayOrValuePointer && dal.has(refArrayOrValuePointer) ? dal.get(refArrayOrValuePointer) : null

        if (!refArray.isRefArray(refArrayOrValue)) {
            return refArrayOrValue ? [refArrayOrValuePointer] : []
        }

        const refArrayToValuesPointers = (valueOrRelationId: string) => {
            const valueOrRelationPointer = pointers.data.getItem(itemType, valueOrRelationId, pageId)
            const valueOrRelation = dal.get(valueOrRelationPointer)

            if (valueOrRelation?.type === RELATION_DATA_TYPES.VARIANTS) {
                const valueId = variantRelation.extractTo(valueOrRelation)
                return pointers.data.getItem(itemType, valueId, pageId)
            }

            return valueOrRelationPointer
        }

        return refArray.extractValuesWithoutHash(refArrayOrValue).map(refArrayToValuesPointers)
    }

    const createEmptyDataItemInOriginalLanguage = (pageId: string, pointer: Pointer, dataType: string): string => {
        const dataId = dataModel.addItem({type: dataType}, DATA_TYPES.data, pageId).id
        dataModel.linkDataToItemByType(pointer, dataId, DATA_TYPES.data, COMP_DATA_QUERY_KEYS_WITH_STYLE.data)
        return dataId
    }

    const getInLanguage = (pageId: string, dataId: string, languageCode?: string): DalItem | undefined => {
        languageCode = languageCode ?? getLanguageByUseOriginal({dal, pointers} as CreateExtArgs, false)
        const originalLanguage = getLanguageByUseOriginal({dal, pointers} as CreateExtArgs, true)
        const gettingDataForSecondaryLanguage = !!languageCode && languageCode !== originalLanguage
        const baseDataItem = dataModel.getItem(dataId, DATA_TYPES.data, pageId, originalLanguage)

        if (gettingDataForSecondaryLanguage) {
            baseDataItem.data = _.assign(
                baseDataItem.data,
                dataModel.getItem(dataId, DATA_TYPES.data, pageId, languageCode)?.data
            )
        }
        return baseDataItem
    }

    const get = (compRef: CompRef, languageCode?: string): DalItem | undefined => {
        const pageId = pointers.structure.getPageOfComponent(compRef).id
        const component = getComp(dal, compRef, onError)
        const dataId = stripHashIfExists(component.dataQuery)

        return getInLanguage(pageId, dataId, languageCode)
    }

    const getItemType = (pointer: Pointer): DalItem['type'] | undefined => {
        const typePointer = pointers.getInnerPointer(pointer, 'type')

        return dal.get(typePointer)
    }

    const getTranslationOverrides = (compRef: CompRef, languageCode: string): DalItem | undefined => {
        validateLanguageCode(languageCode)

        const component = getComp(dal, compRef, onError)
        const pageId = pointers.structure.getPageOfComponent(compRef).id
        const dataId = stripHashIfExists(component.dataQuery)

        return dataModel.getItem(dataId, DATA_TYPES.data, pageId, languageCode)
    }

    const updateInLanguage = (
        compOrInnerElementPointer: Pointer,
        dataItem: DalItem & {type: string},
        pageId: string,
        dataId: string,
        languageCode?: string
    ): void => {
        languageCode = languageCode ?? getLanguageByUseOriginal({dal, pointers} as CreateExtArgs, false)
        const originalLanguage = getLanguageByUseOriginal({dal, pointers} as CreateExtArgs, true)
        const settingDataForSecondaryLanguage = !!languageCode && languageCode !== originalLanguage
        const options: AddItemOptions = {
            skipOriginalNodeMerge: settingDataForSecondaryLanguage
        }

        if (!dataId && settingDataForSecondaryLanguage) {
            dataId = createEmptyDataItemInOriginalLanguage(pageId, compOrInnerElementPointer, dataItem.type)
        }

        const dataPointer = dataModel.addItem(
            dataItem,
            DATA_TYPES.data,
            pageId,
            dataId,
            languageCode,
            undefined,
            options
        )
        if (!settingDataForSecondaryLanguage) {
            dataModel.linkDataToItemByType(
                compOrInnerElementPointer,
                dataPointer.id,
                DATA_TYPES.data,
                COMP_DATA_QUERY_KEYS_WITH_STYLE.data
            )
        }
    }

    const update = (compRef: CompRef, dataItem: DalItem, languageCode?: string): void => {
        if (compRef.type === DATA_TYPES.innerElements) {
            throw new Error('to update inner element data please use the inner elements API')
        }
        const comp = getComp(dal, compRef)
        const expectedDataType = dataItem.type ?? builderTypes(schemaAPI, comp.componentType, onError).data
        const typedDataItem = autoType(dataItem, expectedDataType) as DalItem & {type: string}
        const pageId = pointers.structure.getPageOfComponent(compRef).id
        const dataId = stripHashIfExists(comp.dataQuery)

        updateInLanguage(compRef, typedDataItem, pageId, dataId, languageCode)
    }

    const removeTranslationOverrides = (compRef: CompRef, languageCode: string): void => {
        validateLanguageCode(languageCode)

        const pageId = pointers.structure.getPageOfComponent(compRef).id
        const component = getComp(dal, compRef, onError)
        const dataId = stripHashIfExists(component.dataQuery)
        const translationOverridesPointer = pointers.multilingualTranslations.translationDataItem(
            pageId,
            languageCode,
            dataId
        )
        dataModel.removeItemRecursively(translationOverridesPointer)
    }

    const removeTranslationOverridesForAField = (compRef: CompRef, languageCode: string, field: string): void => {
        validateLanguageCode(languageCode)

        const translationOverridesDataItem = getTranslationOverrides(compRef, languageCode)
        if (translationOverridesDataItem) {
            delete translationOverridesDataItem.data[field]
            if (_.isEmpty(translationOverridesDataItem.data)) {
                removeTranslationOverrides(compRef, languageCode)
            } else {
                update(compRef, translationOverridesDataItem, languageCode)
            }
        }
    }

    return {
        data: {
            get,
            getItemType,
            getInLanguage,
            getTranslationOverrides,
            update,
            updateInLanguage,
            removeTranslationOverrides,
            removeTranslationOverridesForAField,
            query: (namespace: string, pageId: string | null = null, pred?: (a: any) => boolean) => {
                const pageCompFilter = (extensionAPI.page as PageAPI).getPageIndexId(pageId)
                return dal.query(namespace, pageCompFilter, pred)
            },
            queryKeys: (namespace: string, pageId: string | null = null, pred?: KeyValPredicate) => {
                const pageCompFilter = (extensionAPI.page as PageAPI).getPageIndexId(pageId)
                return dal.queryKeys(namespace, pageCompFilter, pred)
            },
            refArray: {
                ...refArray,
                getDataPointersFromRefArray
            },
            getUniqueId,
            breakpointRelation,
            variantRelation: {
                ...variantRelation,
                create: (variants: string[], from: string, to: string) =>
                    variantRelation.create(variants, from, to, true) as VariantRelation
            }
        }
    }
}

const createPublicAPI = ({extensionAPI}: CreateExtArgs): ExtensionPublicAPI => {
    const {data} = extensionAPI as DataExtensionAPI

    return {
        data: {
            get: data.get,
            getItemType: data.getItemType,
            getTranslationOverrides: data.getTranslationOverrides,
            update: data.update,
            removeTranslationOverrides: data.removeTranslationOverrides,
            removeTranslationOverridesForAField: data.removeTranslationOverridesForAField
        }
    }
}

const createExtension = (): Extension => ({
    name: 'data',
    createExtensionAPI,
    createPublicAPI,
    createPointersMethods,
    getDocumentDataTypes
})

export {createExtension}
